// src/Utils/request.tsx
import axios from "axios";
import { beginTrackedRequest, endTrackedRequest } from "./loadingIndicator";
import { clearSessionAndRedirectToLogin } from "./session";
import { notifySuccessForResponse, notifyErrorForResponse } from "./requestFeedback";

export const HTTP_STATUS_CONFLICT = 409;
export const HTTP_STATUS_NOT_MODIFIED = 304;

// Axios defaults to no timeout; callers needing more time (e.g. uploads) can override via config.
export const DEFAULT_REQUEST_TIMEOUT_MS = 180000;

const request = axios.create({ timeout: DEFAULT_REQUEST_TIMEOUT_MS });

// Several call sites build request URLs by concatenating query-string/filter
// values (user-controlled) after a fixed `/api/...` prefix. Those values are
// loosely typed and never validated to be free of a scheme or `//`, so a
// crafted value could otherwise turn the final string into an absolute or
// protocol-relative URL — sending the Authorization bearer token to a
// cross-origin host. Relative, same-origin paths are the only shape every
// legitimate call site needs, so anything else is refused outright.
const ABSOLUTE_OR_PROTOCOL_RELATIVE_URL = /^(?:[a-z][a-z\d+.-]*:)?\/\//i;

request.interceptors.request.use(
  (config) => {
    if (config.url && ABSOLUTE_OR_PROTOCOL_RELATIVE_URL.test(config.url)) {
      return Promise.reject(
        new Error(`Refusing to send request to a cross-origin URL: ${config.url}`)
      );
    }
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    beginTrackedRequest(config?.url);
    return config;
  },
  (error) => {
    endTrackedRequest();
    return Promise.reject(error);
  }
);

request.interceptors.response.use(
  (response) => {
    notifySuccessForResponse(response);
    endTrackedRequest();
    return response;
  },
  (error) => {
    endTrackedRequest();
    if (error.response?.status === 401) {
      clearSessionAndRedirectToLogin();
    }
    notifyErrorForResponse(error);
    return Promise.reject(error);
  }
);

export default request;
